import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { rootNodeDesignerNodeDataOrNullSelector } from "@skilitics/nautilus-core-designer-data";

import {
  buildDesignerNodeSelector,
  getManifestSelector,
  getParentContextByIdSelector,
  getValueSubtypeDefinitionSelector,
} from "../../state/index.ts";
import type { NautilusDesignerAppState } from "../../state/shared.ts";
import {
  getNodeValueSelector,
  resolveAvailableValuesSelector,
  resolveDesignTimeValueSelector,
} from "../valueSelectors.ts";
import { collectValueReferenceEdgesCreate } from "./collectValueReferenceEdges.ts";
import {
  valueReferenceGraphOf,
  wouldCreateValueReferenceCycleCreate,
  type BuildValueReferenceGraph,
} from "./valueReferenceReachability.ts";

/**
 * A way to collect every node-value dependency edge in the current design — not the edges
 * themselves. Collecting them walks the whole design and runs each manifest's binding build,
 * which is far too much to spend on every edit, so the walk waits until something asks for it.
 */
export const valueReferenceGraphSelector: Selector<NautilusDesignerAppState, BuildValueReferenceGraph> = createSelector(
  [
    rootNodeDesignerNodeDataOrNullSelector,
    getManifestSelector,
    buildDesignerNodeSelector,
    getParentContextByIdSelector,
    getNodeValueSelector,
    resolveAvailableValuesSelector,
    resolveDesignTimeValueSelector,
    getValueSubtypeDefinitionSelector,
  ],
  (
    rootNode,
    getManifest,
    buildDesignerNode,
    getParentContextById,
    getNodeValue,
    resolveAvailableValues,
    resolveDesignTimeValue,
    getValueSubtypeDefinition,
  ): BuildValueReferenceGraph =>
    () => {
      if (!rootNode) return valueReferenceGraphOf([]);
      const collectValueReferenceEdges = collectValueReferenceEdgesCreate({
        getManifest,
        buildDesignerNode,
        getParentContextById,
        getNodeValue,
        resolveAvailableValues,
        resolveDesignTimeValue,
        getValueSubtypeDefinition,
      });
      return valueReferenceGraphOf(collectValueReferenceEdges(rootNode));
    },
);

export const useWouldCreateValueReferenceCycle = () => {
  const buildValueReferenceGraph = useSelector(valueReferenceGraphSelector);
  return useMemo(() => wouldCreateValueReferenceCycleCreate(buildValueReferenceGraph), [buildValueReferenceGraph]);
};
